/**
 * @fileoverview Active Games Overview Screen
 * @description Shows game invites, waiting games, active games, and recent games
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Image,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";

/** Game session interface */
interface GameSession {
  id: string;
  game_slug: string;
  mode: "free" | "weekend";
  status: string;
  created_by: string;
  created_at: string;
  completed_at: string | null;
  winner_user_id: string | null;
  game_state: Record<string, unknown>;
  participants: Array<{
    user_id: string;
    name: string;
    avatar_url?: string;
  }>;
}

/** Categorized game data */
interface CategorizedGames {
  invitesReceived: GameSession[];
  waitingForResponse: GameSession[];
  activeGames: GameSession[];
  recentCompleted: GameSession[];
}

/** Game name mapping */
const GAME_NAMES: Record<string, string> = {
  tic_tac_toe: "Boter Kaas en Eieren",
  puzzle_matching: "Memory",
  chess: "Schaken",
  sudoku: "Sudoku",
  quiz: "Quiz",
};

/** Game icon mapping */
const GAME_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  tic_tac_toe: "grid-outline",
  puzzle_matching: "images-outline",
  chess: "extension-puzzle-outline",
  sudoku: "apps-outline",
  quiz: "help-circle-outline",
};

/** Game route mapping */
const GAME_ROUTES: Record<string, string> = {
  tic_tac_toe: "tic-tac-toe",
  puzzle_matching: "memory",
  chess: "chess",
  sudoku: "sudoku",
};

/** Game color mapping */
const GAME_COLORS: Record<string, string> = {
  tic_tac_toe: "#EF4444",
  puzzle_matching: "#3B82F6",
  chess: "#8B5CF6",
  sudoku: "#10B981",
  quiz: "#F59E0B",
};

/**
 * ActiveGamesScreen - Overview of all game-related activities
 * Uses activeProfile to support guardian mode - when a guardian switches to
 * a dependent, shows games for that dependent.
 */
export default function ActiveGamesScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, isSwitched } = useAuth();

  // Use activeProfile for all game queries (supports guardian mode)
  const targetUserId = activeProfile?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [games, setGames] = useState<CategorizedGames>({
    invitesReceived: [],
    waitingForResponse: [],
    activeGames: [],
    recentCompleted: [],
  });

  /**
   * Check if a user is a participant in a game session
   */
  const isUserParticipant = (session: GameSession, userId: string): boolean => {
    // Check participants array
    if (session.participants?.some((p) => p.user_id === userId)) {
      return true;
    }
    // Check game_state for challenger/challenged
    const gameState = session.game_state || {};
    if (gameState.challenger_id === userId || gameState.challenged_user_id === userId) {
      return true;
    }
    // Check created_by
    if (session.created_by === userId) {
      return true;
    }
    return false;
  };

  /**
   * Fetch and categorize all games for the active profile
   */
  const fetchGames = useCallback(async () => {
    if (!targetUserId) return;

    try {
      // Fetch all versus game sessions - RLS will filter to relevant ones
      // We fetch broadly and filter client-side because participants is a JSONB array
      const { data, error } = await supabase
        .from("game_sessions")
        .select("*")
        .eq("session_type", "versus")
        .in("status", ["waiting", "active", "completed"])
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      const sessions = (data || []) as GameSession[];

      // Categorize games - filter to only games where user is a participant
      const categorized: CategorizedGames = {
        invitesReceived: [],
        waitingForResponse: [],
        activeGames: [],
        recentCompleted: [],
      };

      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      sessions.forEach((session) => {
        // Skip if user is not a participant
        if (!isUserParticipant(session, targetUserId)) {
          return;
        }

        const isChallenger = session.created_by === targetUserId;
        const challengedId = session.game_state?.challenged_user_id as string;
        const isChallenged = challengedId === targetUserId;

        if (session.status === "waiting") {
          if (isChallenged) {
            // I was challenged - this is an invite I received
            categorized.invitesReceived.push(session);
          } else if (isChallenger) {
            // I challenged someone - waiting for their response
            categorized.waitingForResponse.push(session);
          }
        } else if (session.status === "active") {
          // Game is in progress
          categorized.activeGames.push(session);
        } else if (session.status === "completed") {
          // Check if completed within last 24 hours
          const completedAt = session.completed_at
            ? new Date(session.completed_at)
            : null;
          if (completedAt && completedAt > twentyFourHoursAgo) {
            categorized.recentCompleted.push(session);
          }
        }
      });

      setGames(categorized);
    } catch (err) {
      console.error("Error fetching games:", err);
    } finally {
      setIsLoading(false);
    }
  }, [targetUserId]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchGames();
    setRefreshing(false);
  }, [fetchGames]);

  /**
   * Subscribe to realtime updates
   */
  useEffect(() => {
    fetchGames();

    if (!targetUserId) return;

    const channel = supabase
      .channel(`active_games_screen_${targetUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
        },
        () => {
          fetchGames();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [targetUserId, fetchGames]);

  /**
   * Navigate to game session
   */
  const handleGamePress = (session: GameSession) => {
    const routePath = GAME_ROUTES[session.game_slug] || session.game_slug;
    router.push(`/(tabs)/games/${routePath}/${session.id}` as any);
  };

  /**
   * Delete/cancel a game session
   * For "invite" type: decline the challenge
   * For "waiting" type: cancel the challenge you sent
   */
  const handleDeleteGame = (session: GameSession, type: "invite" | "waiting") => {
    const isDecline = type === "invite";
    const title = isDecline ? "Uitnodiging weigeren" : "Uitdaging annuleren";
    const message = isDecline
      ? "Weet je zeker dat je deze uitnodiging wilt weigeren?"
      : "Weet je zeker dat je deze uitdaging wilt annuleren?";
    const buttonText = isDecline ? "Weigeren" : "Annuleren";

    Alert.alert(title, message, [
      { text: "Terug", style: "cancel" },
      {
        text: buttonText,
        style: "destructive",
        onPress: async () => {
          try {
            // Delete the game session
            const { error } = await supabase
              .from("game_sessions")
              .delete()
              .eq("id", session.id);

            if (error) throw error;

            // Also delete any related notifications
            const gameState = session.game_state || {};
            const challengedUserId = gameState.challenged_user_id as string;
            
            if (challengedUserId) {
              await supabase
                .from("notifications")
                .delete()
                .eq("type", "game_challenge")
                .or(`action_data->>session_id.eq.${session.id},action_data->>sessionId.eq.${session.id}`);
            }

            // Refresh the list
            fetchGames();
          } catch (err) {
            console.error("Error deleting game:", err);
            Alert.alert("Fout", "Kon de uitdaging niet verwijderen. Probeer het opnieuw.");
          }
        },
      },
    ]);
  };

  /**
   * Get opponent info from session
   */
  const getOpponentInfo = (session: GameSession) => {
    const participants = session.participants || [];
    const opponent = participants.find((p) => p.user_id !== targetUserId);
    
    // Fallback to game_state
    if (!opponent) {
      const gameState = session.game_state || {};
      const isChallenger = session.created_by === targetUserId;
      
      if (isChallenger) {
        return {
          name: (gameState.opponent_name as string) || "Tegenstander",
          avatar_url: null,
        };
      } else {
        return {
          name: (gameState.challenger_name as string) || "Uitdager",
          avatar_url: null,
        };
      }
    }

    return opponent;
  };

  /**
   * Format time ago
   */
  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: nl,
      });
    } catch {
      return "";
    }
  };

  /**
   * Get series info if available
   */
  const getSeriesInfo = (session: GameSession) => {
    const gameState = session.game_state || {};
    const seriesType = gameState.series_type as string;
    
    if (!seriesType || seriesType === "single") return null;
    
    return seriesType === "best_of_3" ? "Best of 3" : "Best of 5";
  };

  /**
   * Render game card
   */
  const renderGameCard = (
    session: GameSession,
    type: "invite" | "waiting" | "active" | "completed"
  ) => {
    const gameName = GAME_NAMES[session.game_slug] || session.game_slug;
    const gameIcon = GAME_ICONS[session.game_slug] || "game-controller-outline";
    const gameColor = GAME_COLORS[session.game_slug] || colors.primary;
    const opponent = getOpponentInfo(session);
    const seriesInfo = getSeriesInfo(session);

    const isWinner = session.winner_user_id === targetUserId;
    const isDraw = session.status === "completed" && !session.winner_user_id;

    return (
      <TouchableOpacity
        key={session.id}
        onPress={() => handleGamePress(session)}
        activeOpacity={0.7}
        className="rounded-xl p-4 mb-3"
        style={{
          backgroundColor: colors.surface,
          borderLeftWidth: 4,
          borderLeftColor: gameColor,
        }}
      >
        <View className="flex-row items-center">
          {/* Game Icon */}
          <View
            className="w-12 h-12 rounded-xl items-center justify-center mr-3"
            style={{ backgroundColor: `${gameColor}20` }}
          >
            <Ionicons name={gameIcon} size={24} color={gameColor} />
          </View>

          {/* Game Info */}
          <View className="flex-1">
            <View className="flex-row items-center">
              <Text
                className="font-semibold text-base"
                style={{ color: colors.text }}
              >
                {gameName}
              </Text>
              {seriesInfo && (
                <View
                  className="ml-2 px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${gameColor}20` }}
                >
                  <Text className="text-xs font-medium" style={{ color: gameColor }}>
                    {seriesInfo}
                  </Text>
                </View>
              )}
            </View>

            <View className="flex-row items-center mt-1">
              {opponent.avatar_url ? (
                <Image
                  source={{ uri: opponent.avatar_url }}
                  className="w-5 h-5 rounded-full mr-1.5"
                />
              ) : (
                <View
                  className="w-5 h-5 rounded-full mr-1.5 items-center justify-center"
                  style={{ backgroundColor: colors.primaryLight }}
                >
                  <Text
                    className="text-xs font-bold"
                    style={{ color: colors.primary }}
                  >
                    {opponent.name?.charAt(0) || "?"}
                  </Text>
                </View>
              )}
              <Text
                className="text-sm"
                style={{ color: colors.textSecondary }}
              >
                {type === "invite"
                  ? `${opponent.name} daagt je uit`
                  : type === "waiting"
                  ? `Wachten op ${opponent.name}`
                  : type === "active"
                  ? `vs ${opponent.name}`
                  : isWinner
                  ? `Gewonnen van ${opponent.name}`
                  : isDraw
                  ? `Gelijkspel met ${opponent.name}`
                  : `Verloren van ${opponent.name}`}
              </Text>
            </View>

            <Text
              className="text-xs mt-1"
              style={{ color: colors.textTertiary }}
            >
              {formatTimeAgo(session.created_at)}
            </Text>
          </View>

          {/* Status Badge / Action */}
          <View className="flex-row items-center">
            {/* Delete button for invite and waiting types */}
            {(type === "invite" || type === "waiting") && (
              <TouchableOpacity
                onPress={(e) => {
                  e.stopPropagation();
                  handleDeleteGame(session, type);
                }}
                className="p-2 mr-1"
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityLabel={type === "invite" ? "Weigeren" : "Annuleren"}
              >
                <Ionicons name="trash-outline" size={18} color="#EF4444" />
              </TouchableOpacity>
            )}
            
            {type === "invite" && (
              <View
                className="px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: "#22C55E" }}
              >
                <Text className="text-white font-semibold text-sm">Spelen</Text>
              </View>
            )}
            {type === "waiting" && (
              <View className="items-center">
                <Ionicons name="hourglass-outline" size={20} color="#F59E0B" />
                <Text
                  className="text-xs mt-0.5"
                  style={{ color: colors.textSecondary }}
                >
                  Wachten
                </Text>
              </View>
            )}
            {type === "active" && (
              <View
                className="px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: colors.primary }}
              >
                <Text className="text-white font-semibold text-sm">
                  Verder
                </Text>
              </View>
            )}
            {type === "completed" && (
              <View className="items-center">
                {isWinner ? (
                  <Ionicons name="trophy" size={24} color="#22C55E" />
                ) : isDraw ? (
                  <Ionicons name="remove-circle-outline" size={24} color="#F59E0B" />
                ) : (
                  <Ionicons name="close-circle-outline" size={24} color="#EF4444" />
                )}
              </View>
            )}
          </View>
        </View>

        {/* Weekend mode badge */}
        {session.mode === "weekend" && (
          <View className="flex-row items-center mt-2 pt-2 border-t" style={{ borderTopColor: colors.border }}>
            <Ionicons name="trophy-outline" size={12} color="#F59E0B" />
            <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
              Weekend competitie
            </Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  /**
   * Render section
   */
  const renderSection = (
    title: string,
    icon: keyof typeof Ionicons.glyphMap,
    iconColor: string,
    sessions: GameSession[],
    type: "invite" | "waiting" | "active" | "completed",
    emptyMessage: string
  ) => {
    return (
      <View className="mb-6">
        <View className="flex-row items-center mb-3">
          <View
            className="w-8 h-8 rounded-lg items-center justify-center mr-2"
            style={{ backgroundColor: `${iconColor}20` }}
          >
            <Ionicons name={icon} size={18} color={iconColor} />
          </View>
          <Text className="font-bold text-lg" style={{ color: colors.text }}>
            {title}
          </Text>
          {sessions.length > 0 && (
            <View
              className="ml-2 px-2 py-0.5 rounded-full"
              style={{ backgroundColor: iconColor }}
            >
              <Text className="text-white text-xs font-bold">
                {sessions.length}
              </Text>
            </View>
          )}
        </View>

        {sessions.length === 0 ? (
          <View
            className="rounded-xl p-6 items-center"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
              borderStyle: "dashed",
            }}
          >
            <Ionicons
              name="game-controller-outline"
              size={32}
              color={colors.textTertiary}
            />
            <Text
              className="text-center mt-2"
              style={{ color: colors.textSecondary }}
            >
              {emptyMessage}
            </Text>
          </View>
        ) : (
          sessions.map((session) => renderGameCard(session, type))
        )}
      </View>
    );
  };

  const totalPending = games.invitesReceived.length + games.waitingForResponse.length + games.activeGames.length;

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderBottomColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => {
            // Check if we can go back, otherwise navigate to games tab
            if (router.canGoBack()) {
              router.back();
            } else {
              router.replace("/(tabs)/games");
            }
          }}
          className="p-2 -ml-2 mr-2"
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View className="flex-1">
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            Mijn Spellen
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            {totalPending > 0
              ? `${totalPending} actieve ${totalPending === 1 ? "spel" : "spellen"}`
              : "Geen actieve spellen"}
          </Text>
        </View>
        
        {/* Playing as child badge */}
        {isSwitched && activeProfile && (
          <View
            className="flex-row items-center px-3 py-1.5 rounded-full"
            style={{ backgroundColor: "#DBEAFE" }}
          >
            <Ionicons name="person" size={14} color="#1D4ED8" />
            <Text className="text-sm font-medium ml-1" style={{ color: "#1D4ED8" }}>
              {activeProfile.full_name}
            </Text>
          </View>
        )}
      </View>

      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
          <Text className="mt-4" style={{ color: colors.textSecondary }}>
            Laden...
          </Text>
        </View>
      ) : (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ padding: 16 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {/* Invites Received */}
          {renderSection(
            "Uitnodigingen",
            "mail-outline",
            "#22C55E",
            games.invitesReceived,
            "invite",
            "Geen uitnodigingen ontvangen"
          )}

          {/* Waiting for Response */}
          {renderSection(
            "Wachten op Reactie",
            "hourglass-outline",
            "#F59E0B",
            games.waitingForResponse,
            "waiting",
            "Geen uitdagingen verstuurd"
          )}

          {/* Active Games */}
          {renderSection(
            "Actieve Spellen",
            "play-circle-outline",
            colors.primary,
            games.activeGames,
            "active",
            "Geen actieve spellen"
          )}

          {/* Recently Completed */}
          {renderSection(
            "Recent Afgerond",
            "checkmark-circle-outline",
            "#6B7280",
            games.recentCompleted,
            "completed",
            "Geen recent afgeronde spellen"
          )}

          {/* Quick Actions */}
          <View className="mt-2 mb-4">
            <Text className="font-bold text-lg mb-3" style={{ color: colors.text }}>
              Nieuw Spel Starten
            </Text>
            <View className="flex-row flex-wrap gap-3">
              <TouchableOpacity
                onPress={() => router.push("/(tabs)/games/tic-tac-toe")}
                className="flex-1 rounded-xl p-4 items-center min-w-[100px]"
                style={{ backgroundColor: `${GAME_COLORS.tic_tac_toe}15` }}
              >
                <Ionicons
                  name="grid-outline"
                  size={28}
                  color={GAME_COLORS.tic_tac_toe}
                />
                <Text
                  className="text-sm font-medium mt-2 text-center"
                  style={{ color: colors.text }}
                >
                  Boter Kaas
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => router.push("/(tabs)/games/memory")}
                className="flex-1 rounded-xl p-4 items-center min-w-[100px]"
                style={{ backgroundColor: `${GAME_COLORS.puzzle_matching}15` }}
              >
                <Ionicons
                  name="images-outline"
                  size={28}
                  color={GAME_COLORS.puzzle_matching}
                />
                <Text
                  className="text-sm font-medium mt-2 text-center"
                  style={{ color: colors.text }}
                >
                  Memory
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => router.push("/(tabs)/games")}
                className="flex-1 rounded-xl p-4 items-center min-w-[100px]"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons
                  name="apps-outline"
                  size={28}
                  color={colors.textSecondary}
                />
                <Text
                  className="text-sm font-medium mt-2 text-center"
                  style={{ color: colors.text }}
                >
                  Meer...
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <View className="h-8" />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

